/**
 * Sprint 12+M5.20 (27.07.2026) — Fokustyp-Referenz-Grid.
 *
 * Dezente Visualisierung der 4 Fokustypen im Pionier-Cockpit, damit der
 * User visuell erkennt: "Das ist mein Modell". Aktiver Fokustyp bekommt
 * Gold-Outline; die anderen bleiben sekundaer.
 *
 * Sprint M5.20c (27.07.2026) — Originaltypen-Icons.
 *   Andreas' Freigabe: nutze die vorhandenen Original-Fokustyp-PNGs unter
 *   /public/icons/icon-{katze,eule,biene,hund}.png (nicht Lucide-Icons).
 *   Lucide bleibt nur als LAST-RESORT-Fallback falls das PNG mal 404 wirft.
 */
import React, { useState } from "react";
import { Cat, Bird, Bug, Dog } from "lucide-react";

const FOKUSTYPEN = [
  {
    key: "katze",
    label: "Die Katze",
    subline: "Impulsiv, bevor du nachdenkst",
    icon: Cat,
    assetSrc: "/icons/icon-katze.png",
    accent: "#E84B25",
  },
  {
    key: "eule",
    label: "Die Eule",
    subline: "Sache vor Menschen",
    icon: Bird,
    assetSrc: "/icons/icon-eule.png",
    accent: "#4563BB",
  },
  {
    key: "biene",
    label: "Die Biene",
    subline: "Klarheit vor Aktion",
    icon: Bug,
    assetSrc: "/icons/icon-biene.png",
    accent: "#F59E0B",
  },
  {
    key: "hund",
    label: "Der Hund",
    subline: "Da sein statt Nein sagen",
    icon: Dog,
    assetSrc: "/icons/icon-hund.png",
    accent: "#10B981",
  },
];

const SLUG_MAP = {
  cat: "katze",
  owl: "eule",
  bee: "biene",
  dog: "hund",
};

function normalize(slug) {
  if (!slug) return "";
  const s = String(slug).toLowerCase();
  return SLUG_MAP[s] || s;
}

function IconOrAsset({ item, isActive }) {
  const [assetFailed, setAssetFailed] = useState(false);
  const IconCmp = item.icon;
  if (assetFailed) {
    return (
      <IconCmp
        className={`w-10 h-10 sm:w-12 sm:h-12 transition-colors ${
          isActive ? "text-white" : "text-slate-500"
        }`}
        strokeWidth={1.6}
      />
    );
  }
  return (
    <img
      src={item.assetSrc}
      alt={item.label}
      onError={() => setAssetFailed(true)}
      className={`w-14 h-14 sm:w-16 sm:h-16 object-contain select-none transition-opacity ${
        isActive ? "opacity-100" : "opacity-85"
      }`}
      draggable="false"
    />
  );
}

export default function FokustypReferenzGrid({ activeFokustyp }) {
  const active = normalize(activeFokustyp);

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7"
      data-testid="fokustyp-referenz-grid"
      aria-label="Dein Modell: Die vier Fokustypen"
    >
      <header className="text-center mb-5 sm:mb-6">
        <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
          Dein Modell
        </p>
        <h3 className="mt-1 text-lg sm:text-xl font-bold text-slate-900">
          Die vier Fokustypen
        </h3>
        {active ? (
          <p
            className="mt-1 text-xs sm:text-sm text-slate-500"
            data-testid="fokustyp-referenz-active-hint"
          >
            Dein Fokustyp ist markiert.
          </p>
        ) : null}
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {FOKUSTYPEN.map((item) => {
          const isActive = active === item.key;
          return (
            <div
              key={item.key}
              data-testid={`fokustyp-tile-${item.key}`}
              className={`relative flex flex-col items-center gap-2 rounded-xl p-3 sm:p-4 border-2 transition-all ${
                isActive
                  ? "border-[#f97316] shadow-[0_0_18px_rgba(249,115,22,0.35)] bg-gradient-to-br from-[#1a1a1a] to-[#2a1a10]"
                  : "border-slate-200 bg-slate-50 hover:border-slate-300"
              }`}
              style={isActive ? { boxShadow: `0 0 0 2px ${item.accent}22, 0 0 22px rgba(249,115,22,0.28)` } : undefined}
            >
              {isActive && (
                <span
                  className="absolute -top-2 -right-2 inline-flex items-center gap-1 rounded-full bg-[#f97316] text-white text-[10px] font-semibold px-2 py-0.5 shadow"
                  data-testid={`fokustyp-tile-active-badge-${item.key}`}
                >
                  Dein Primär-Fokustyp
                </span>
              )}
              <IconOrAsset item={item} isActive={isActive} />
              <div className="text-center">
                <div
                  className={`text-sm font-bold leading-tight ${
                    isActive ? "text-white" : "text-slate-800"
                  }`}
                >
                  {item.label}
                </div>
                <div
                  className={`mt-0.5 text-[11px] sm:text-xs leading-snug ${
                    isActive ? "text-slate-200" : "text-slate-500"
                  }`}
                >
                  {item.subline}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
